import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/authContext';
import type { ApiResult } from '@/services/apiClient';
import { m365WorkspaceApi } from './m365Workspace.api';
import { getSessionOriginKey } from '@/services/sessionOrigin';
import { rememberM365Return } from './m365OAuthReturn';
import type { M365MailMessage } from './m365Workspace.types';

export const m365QueryKeys = {
  all: ['m365-workspace'] as const,
  status: () => [...m365QueryKeys.all, 'status'] as const,
  digest: () => [...m365QueryKeys.all, 'activity-digest'] as const,
  mailFolders: () => [...m365QueryKeys.all, 'mail-folders'] as const,
  mailMessages: (folderId: string | null, search: string) =>
    [...m365QueryKeys.all, 'mail-messages', folderId ?? 'inbox', search] as const,
  mailMessage: (id: string | null) => [...m365QueryKeys.all, 'mail-message', id ?? 'none'] as const,
  calendar: (start: string, end: string) => [...m365QueryKeys.all, 'calendar', start, end] as const,
  todoLists: () => [...m365QueryKeys.all, 'todo-lists'] as const,
  todoTasks: (listId: string | null) =>
    [...m365QueryKeys.all, 'todo-tasks', listId ?? 'none'] as const,
  notebooks: () => [...m365QueryKeys.all, 'notebooks'] as const,
  sections: (notebookId: string | null) =>
    [...m365QueryKeys.all, 'sections', notebookId ?? 'none'] as const,
  pages: (sectionId: string | null) =>
    [...m365QueryKeys.all, 'pages', sectionId ?? 'none'] as const,
  pageContent: (pageId: string | null) =>
    [...m365QueryKeys.all, 'page-content', pageId ?? 'none'] as const,
  files: (itemId: string | null) => [...m365QueryKeys.all, 'files', itemId ?? 'root'] as const,
  sharePointSites: (query: string) => [...m365QueryKeys.all, 'sharepoint-sites', query] as const,
  sharePointLibraries: (siteId: string | null) =>
    [...m365QueryKeys.all, 'sharepoint-libraries', siteId ?? 'none'] as const,
  sharePointItems: (driveId: string | null, itemId: string | null) =>
    [...m365QueryKeys.all, 'sharepoint-items', driveId ?? 'none', itemId ?? 'root'] as const,
};

function unwrap<T>(result: ApiResult<T>, fallback: string): T {
  if (!result.ok || result.data == null) {
    throw new Error(result.error ?? fallback);
  }
  return result.data;
}

function useM365QueryScope() {
  const { user, tenant, permissions } = useAuth();
  return [
    tenant?.id ?? user?.tenantId,
    user?.id,
    getSessionOriginKey(),
    JSON.stringify([...permissions].sort()),
  ] as const;
}

export function useM365Status(enabled: boolean) {
  const scope = useM365QueryScope();
  return useQuery({
    queryKey: [...m365QueryKeys.status(), ...scope],
    enabled,
    queryFn: async () => unwrap(await m365WorkspaceApi.status(), 'Unable to load M365 status'),
    staleTime: 20_000,
    refetchOnMount: 'always',
    refetchInterval: 60_000,
  });
}

export function useM365ActivityDigest(enabled: boolean) {
  const scope = useM365QueryScope();
  return useQuery({
    queryKey: [...m365QueryKeys.digest(), ...scope],
    enabled,
    refetchInterval: 60_000,
    queryFn: async () =>
      unwrap(await m365WorkspaceApi.activityDigest(), 'Unable to load Microsoft 365 activity'),
  });
}

export function useM365Connect() {
  const client = useQueryClient();
  const auth = useAuth();
  return useMutation({
    mutationFn: async (returnTo: string | void) => {
      const origin = getSessionOriginKey();
      const url = unwrap(await m365WorkspaceApi.oauthUrl(), 'Unable to start Microsoft sign-in');
      if (!url.authorizationUrl) {
        throw new Error(url.detail || 'Microsoft OAuth is not configured');
      }
      if (origin !== getSessionOriginKey())
        throw new Error('Your sign-in changed. Start Microsoft connection again.');
      if (auth.user)
        rememberM365Return(
          url.state,
          { tenantId: auth.user.tenantId, userId: auth.user.id },
          returnTo || undefined,
        );
      window.location.assign(url.authorizationUrl);
      return url;
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: m365QueryKeys.status() });
    },
  });
}

export function useM365OAuthCallback() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { code: string; state: string }) => {
      const origin = getSessionOriginKey();
      const result = unwrap(
        await m365WorkspaceApi.oauthCallback(input),
        'Unable to complete Microsoft sign-in',
      );
      if (origin !== getSessionOriginKey())
        throw new Error('Your sign-in changed. Reconnect Microsoft with your current session.');
      return result;
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: m365QueryKeys.all });
    },
  });
}

export function useM365Disconnect() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      unwrap(await m365WorkspaceApi.disconnect(), 'Unable to disconnect Microsoft account'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: m365QueryKeys.all });
    },
  });
}

export function useM365MailFolders(enabled: boolean) {
  const scope = useM365QueryScope();
  return useQuery({
    queryKey: [...m365QueryKeys.mailFolders(), ...scope],
    enabled,
    // The server already bounds read-only throttling retries for this traversal.
    retry: false,
    refetchInterval: 60_000,
    queryFn: async () =>
      unwrap(await m365WorkspaceApi.mailFolders(), 'Unable to load mail folders'),
  });
}

export function useM365MailMessages(folderId: string | null, enabled: boolean, search = '') {
  const scope = useM365QueryScope();
  return useInfiniteQuery<
    { items: M365MailMessage[]; nextCursor: string | null },
    Error,
    M365MailMessage[],
    readonly unknown[],
    string | undefined
  >({
    queryKey: [...m365QueryKeys.mailMessages(folderId, search), ...scope],
    enabled,
    refetchInterval: 60_000,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    select: (data) => data.pages.flatMap((page) => page.items),
    queryFn: async ({ pageParam }) =>
      unwrap(
        await m365WorkspaceApi.mailMessagesPage({
          folderId: folderId ?? undefined,
          top: 40,
          search: search || undefined,
          cursor: pageParam,
        }),
        'Unable to load messages',
      ),
  });
}

export function useM365ApplyCrm() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      m365WorkspaceApi
        .crmSeedApply(ids)
        .then((result) => unwrap(result, 'Unable to record email in CRM')),
    onSuccess: () => client.invalidateQueries({ queryKey: ['crm'] }),
  });
}

export function useM365MailMessage(messageId: string | null, enabled: boolean) {
  const scope = useM365QueryScope();
  return useQuery({
    queryKey: [...m365QueryKeys.mailMessage(messageId), ...scope],
    enabled: enabled && Boolean(messageId),
    queryFn: async () =>
      unwrap(await m365WorkspaceApi.mailMessage(messageId!), 'Unable to load message'),
  });
}

export function useM365SendMail() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { to: string[]; cc?: string[]; subject: string; bodyHtml: string }) =>
      unwrap(await m365WorkspaceApi.sendMail(input), 'Unable to send mail'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: m365QueryKeys.all });
    },
  });
}

export function useM365ReplyMail() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { messageId: string; bodyHtml: string; replyAll?: boolean }) =>
      unwrap(
        await m365WorkspaceApi.replyMail(input.messageId, {
          bodyHtml: input.bodyHtml,
          replyAll: input.replyAll,
        }),
        'Unable to send reply',
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: m365QueryKeys.all });
    },
  });
}

export function useM365MarkRead() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { messageId: string; isRead: boolean }) =>
      unwrap(
        await m365WorkspaceApi.markRead(input.messageId, input.isRead),
        'Unable to update read state',
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: m365QueryKeys.all });
    },
  });
}

export function useM365MoveMail() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { messageId: string; destinationFolderId: string }) =>
      unwrap(
        await m365WorkspaceApi.moveMail(input.messageId, input.destinationFolderId),
        'Unable to move message',
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: m365QueryKeys.all });
    },
  });
}

export function useM365Calendar(start: string, end: string, enabled: boolean) {
  const scope = useM365QueryScope();
  return useQuery({
    queryKey: [...m365QueryKeys.calendar(start, end), ...scope],
    enabled,
    queryFn: async () =>
      unwrap(await m365WorkspaceApi.calendarEvents(start, end), 'Unable to load calendar'),
  });
}

export function useM365CreateEvent() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { subject: string; start: string; end: string; location?: string }) =>
      unwrap(await m365WorkspaceApi.createEvent(input), 'Unable to create event'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: m365QueryKeys.all });
    },
  });
}

export function useM365UpdateEvent() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      eventId: string;
      subject?: string;
      start?: string;
      end?: string;
      location?: string;
    }) =>
      unwrap(
        await m365WorkspaceApi.updateEvent(input.eventId, {
          subject: input.subject,
          start: input.start,
          end: input.end,
          location: input.location,
        }),
        'Unable to update event',
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: m365QueryKeys.all });
    },
  });
}

export function useM365DeleteEvent() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (eventId: string) =>
      unwrap(await m365WorkspaceApi.deleteEvent(eventId), 'Unable to delete event'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: m365QueryKeys.all });
    },
  });
}

export function useM365TodoLists(enabled: boolean) {
  const scope = useM365QueryScope();
  return useQuery({
    queryKey: [...m365QueryKeys.todoLists(), ...scope],
    enabled,
    queryFn: async () => unwrap(await m365WorkspaceApi.todoLists(), 'Unable to load To Do lists'),
  });
}

export function useM365TodoTasks(listId: string | null, enabled: boolean) {
  const scope = useM365QueryScope();
  return useQuery({
    queryKey: [...m365QueryKeys.todoTasks(listId), ...scope],
    enabled: enabled && Boolean(listId),
    queryFn: async () => unwrap(await m365WorkspaceApi.todoTasks(listId!), 'Unable to load tasks'),
  });
}

export function useM365CreateTask() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      listId: string;
      title: string;
      dueDateTime?: string;
      importance?: 'low' | 'normal' | 'high';
    }) =>
      unwrap(
        await m365WorkspaceApi.createTask(input.listId, {
          title: input.title,
          dueDateTime: input.dueDateTime,
          importance: input.importance,
        }),
        'Unable to create task',
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: m365QueryKeys.all });
    },
  });
}

export function useM365CompleteTask() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { listId: string; taskId: string; status?: string }) =>
      unwrap(
        await m365WorkspaceApi.updateTask(input.listId, input.taskId, {
          status: input.status ?? 'completed',
        }),
        'Unable to update task',
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: m365QueryKeys.all });
    },
  });
}

export function useM365Notebooks(enabled: boolean) {
  const scope = useM365QueryScope();
  return useQuery({
    queryKey: [...m365QueryKeys.notebooks(), ...scope],
    enabled,
    queryFn: async () => unwrap(await m365WorkspaceApi.notebooks(), 'Unable to load notebooks'),
  });
}

export function useM365Sections(notebookId: string | null, enabled: boolean) {
  const scope = useM365QueryScope();
  return useQuery({
    queryKey: [...m365QueryKeys.sections(notebookId), ...scope],
    enabled: enabled && Boolean(notebookId),
    queryFn: async () =>
      unwrap(await m365WorkspaceApi.sections(notebookId!), 'Unable to load sections'),
  });
}

export function useM365Pages(sectionId: string | null, enabled: boolean) {
  const scope = useM365QueryScope();
  return useQuery({
    queryKey: [...m365QueryKeys.pages(sectionId), ...scope],
    enabled: enabled && Boolean(sectionId),
    queryFn: async () => unwrap(await m365WorkspaceApi.pages(sectionId!), 'Unable to load pages'),
  });
}

export function useM365PageContent(pageId: string | null, enabled: boolean) {
  const scope = useM365QueryScope();
  return useQuery({
    queryKey: [...m365QueryKeys.pageContent(pageId), ...scope],
    enabled: enabled && Boolean(pageId),
    queryFn: async () => unwrap(await m365WorkspaceApi.pageContent(pageId!), 'Unable to load page'),
  });
}

export function useM365CreatePage() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { sectionId: string; title: string; html: string }) =>
      unwrap(
        await m365WorkspaceApi.createPage(input.sectionId, {
          title: input.title,
          html: input.html,
        }),
        'Unable to create page',
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: m365QueryKeys.all });
    },
  });
}

export function useM365Files(itemId: string | null, enabled: boolean) {
  const scope = useM365QueryScope();
  return useQuery({
    queryKey: [...m365QueryKeys.files(itemId), ...scope],
    enabled,
    queryFn: async () =>
      unwrap(await m365WorkspaceApi.listFiles(itemId ?? undefined), 'Unable to load OneDrive'),
  });
}

export function useM365DownloadUrl() {
  return useMutation({
    mutationFn: async (itemId: string) =>
      unwrap(await m365WorkspaceApi.downloadUrl(itemId), 'Unable to get download URL'),
  });
}

export function useM365RenameFile() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { itemId: string; name: string }) =>
      unwrap(await m365WorkspaceApi.renameFile(input.itemId, input.name), 'Unable to rename file'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: m365QueryKeys.all });
    },
  });
}

export function useM365DeleteFile() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (itemId: string) =>
      unwrap(await m365WorkspaceApi.deleteFile(itemId), 'Unable to delete file'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: m365QueryKeys.all });
    },
  });
}

export function useM365SyncToLibrary() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { itemIds?: string[]; fullDelta?: boolean } = {}) =>
      unwrap(await m365WorkspaceApi.syncToLibrary(input), 'Unable to sync to Document Library'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: m365QueryKeys.status() });
      void client.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}

export function useM365SharePointSites(query: string, enabled: boolean) {
  const scope = useM365QueryScope();
  return useQuery({
    queryKey: [...m365QueryKeys.sharePointSites(query), ...scope],
    enabled,
    queryFn: async () =>
      unwrap(
        await m365WorkspaceApi.sharePointSites(query || undefined),
        'Unable to load SharePoint sites',
      ),
  });
}

export function useM365SharePointLibraries(siteId: string | null, enabled: boolean) {
  const scope = useM365QueryScope();
  return useQuery({
    queryKey: [...m365QueryKeys.sharePointLibraries(siteId), ...scope],
    enabled: enabled && Boolean(siteId),
    queryFn: async () =>
      unwrap(await m365WorkspaceApi.sharePointLibraries(siteId!), 'Unable to load libraries'),
  });
}

export function useM365SharePointItems(
  driveId: string | null,
  itemId: string | null,
  enabled: boolean,
) {
  const scope = useM365QueryScope();
  return useQuery({
    queryKey: [...m365QueryKeys.sharePointItems(driveId, itemId), ...scope],
    enabled: enabled && Boolean(driveId),
    queryFn: async () =>
      unwrap(
        await m365WorkspaceApi.sharePointItems(driveId!, itemId ?? undefined),
        'Unable to load SharePoint files',
      ),
  });
}

export function useM365SharePointDownload() {
  return useMutation({
    mutationFn: async (input: { driveId: string; itemId: string }) =>
      unwrap(
        await m365WorkspaceApi.sharePointDownloadUrl(input.driveId, input.itemId),
        'Unable to get SharePoint download URL',
      ),
  });
}
